/**
 * xceller. API Service
 * Handles all communication between React frontend and Flask backend
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

class ApiService {
  /**
   * Generic fetch wrapper with error handling
   */
  async fetch(endpoint, options = {}) {
    try {
      const token = localStorage.getItem('xceller_token');
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...options.headers,
        },
        ...options,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API Error (${endpoint}):`, error);
      throw error;
    }
  }

  // ==================== STATUS ====================

  /**
   * Get system status
   * @returns {Promise<{running: boolean, telegram_connected: boolean, mt5_connected: boolean}>}
   */
  async getStatus() {
    return this.fetch('/status');
  }

  /**
   * Start the trading system
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async start() {
    return this.fetch('/start', { method: 'POST' });
  }

  /**
   * Stop the trading system
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async stop() {
    return this.fetch('/stop', { method: 'POST' });
  }

  // ==================== STATS ====================

  /**
   * Get trading statistics
   * @returns {Promise<{total_signals: number, executed: number, profit: number, win_rate: number}>}
   */
  async getStats() {
    return this.fetch('/stats');
  }

  // ==================== SIGNALS ====================

  /**
   * Get all signals
   * @returns {Promise<Array>}
   */
  async getSignals() {
    return this.fetch('/signals');
  }

  /**
   * Get daily signal audit entries
   * @param {string} date - YYYY-MM-DD
   * @returns {Promise<{success: boolean, date: string, entries: Array, count: number}>}
   */
  async getSignalAudit(date) {
    const query = date ? `?date=${encodeURIComponent(date)}` : '';
    return this.fetch(`/signal-audit${query}`);
  }

  /**
   * Test signal parsing
   * @param {string} text - Text to parse
   * @returns {Promise<{success: boolean, signal?: object, message: string}>}
   */
  async testSignal(text) {
    return this.fetch('/test-signal', {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  }

  // ==================== CONFIG ====================

  /**
   * Get configuration
   * @returns {Promise<object>}
   */
  async getConfig() {
    return this.fetch('/config');
  }

  /**
   * Update configuration
   * @param {object} config - Configuration object
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async updateConfig(config) {
    return this.fetch('/config', {
      method: 'POST',
      body: JSON.stringify(config),
    });
  }

  /**
   * Get Telegram chats/channels available to the current session
   * @returns {Promise<{success: boolean, chats: Array, count: number}>}
   */
  async getTelegramChats() {
    return this.fetch('/telegram/chats');
  }

  // ==================== NOTIFICATIONS ====================

  /**
   * Get recent notifications
   * @returns {Promise<Array>}
   */
  async getNotifications() {
    return this.fetch('/notifications');
  }

  // ==================== MT5 ====================

  /**
   * Get current MT5 positions
   * @returns {Promise<Array>}
   */
  async getPositions() {
    return this.fetch('/positions');
  }

  /**
   * Close an MT5 position by ticket
   * @param {number} ticket
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async closePosition(ticket) {
    return this.fetch(`/positions/${ticket}/close`, { method: 'POST' });
  }

  /**
   * Move SL to entry for a single open position. Bypasses provider scope.
   */
  async breakevenPosition(ticket) {
    return this.fetch(`/positions/${ticket}/breakeven`, { method: 'POST' });
  }

  /**
   * Get current bid/ask for a symbol (e.g. "XAUUSD")
   * @param {string} symbol
   * @returns {Promise<{symbol: string, bid: number, ask: number, spread: number, time: number}>}
   */
  async getPrice(symbol) {
    return this.fetch(`/price/${encodeURIComponent(symbol)}`);
  }

  /**
   * Get live MT5 account info (balance, equity, floating P&L)
   * @returns {Promise<{balance: number, equity: number, profit: number, currency: string, server: string}>}
   */
  async getAccount() {
    return this.fetch('/account');
  }

  /**
   * Get closed trade history from MT5 (last 30 days)
   * @returns {Promise<Array>}
   */
  async getHistory() {
    return this.fetch('/history');
  }

  /**
   * Get per-provider daily recap (trades, P&L, mgmt-command stats)
   * @param {string} [date] - YYYY-MM-DD (defaults to today)
   */
  async getDailyRecap(date) {
    const query = date ? `?date=${encodeURIComponent(date)}` : '';
    return this.fetch(`/daily-recap${query}`);
  }

  // ==================== HEALTH ====================

  /**
   * Health check
   * @returns {Promise<{status: string, timestamp: string}>}
   */
  async healthCheck() {
    return this.fetch('/health');
  }

  // ==================== POLLING ====================

  /**
   * Start polling for real-time updates
   * @param {Function} onUpdate - Callback function called with updates
   * @param {number} interval - Polling interval in ms (default: 2000)
   * @returns {Function} - Function to stop polling
   */
  startPolling(onUpdate, interval = 2000) {
    let isPolling = true;

    const safe = (p) => p.catch(() => null);
    const poll = async () => {
      while (isPolling) {
        try {
          const [status, stats, signals, notifications, account, positions, goldPrice] = await Promise.all([
            this.getStatus(),
            this.getStats(),
            this.getSignals(),
            this.getNotifications(),
            this.getAccount(),
            safe(this.getPositions()),
            safe(this.getPrice('XAUUSD')),
          ]);

          onUpdate({
            status,
            stats,
            signals,
            notifications,
            account,
            positions: positions || [],
            goldPrice: goldPrice || null,
            timestamp: Date.now(),
          });
        } catch (error) {
          console.error('Polling error:', error);
          onUpdate({ error: error.message });
        }

        await new Promise(resolve => setTimeout(resolve, interval));
      }
    };

    poll();

    // Return stop function
    return () => {
      isPolling = false;
    };
  }
}

// Export singleton instance
export default new ApiService();
